import { findConsecutiveRepeatedCharacter, findRepeatedWord } from './descriptionValidation';
import type { AddressGeoLevelDef } from '../services/platform/masterService';

export interface AddressFieldExistingRecord {
  _id?: string;
  code?: string;
  name?: string;
  sortOrder?: number;
  isActive?: boolean;
  metadata?: Record<string, unknown>;
}

export interface AddressFieldFormInput {
  name: string;
  description?: string;
  sortOrder: number;
  metadata: Record<string, string | number | boolean>;
}

export const ADDRESS_FIELD_LABELS: Record<string, string> = {
  geoLevel: 'Geo Level',
  name: 'Display name',
  fieldKey: 'Field Key',
  fieldType: 'Field Type',
  source: 'Data Source',
  required: 'Required',
  sortOrder: 'Sort order',
  description: 'Description',
};

export const ADDRESS_GEO_LEVEL_LABELS: Record<string, { label: string; parent?: string }> = {
  country: { label: 'Country' },
  state: { label: 'State / Province', parent: 'country' },
  city: { label: 'City', parent: 'state' },
  district: { label: 'District / Locality', parent: 'city' },
  pincode: { label: 'Pincode / Postal code', parent: 'country' },
  line1: { label: 'Address line 1' },
  line2: { label: 'Address line 2' },
  landmark: { label: 'Landmark' },
  custom: { label: 'Custom field' },
};

export const ADDRESS_FIELD_TYPE_LABELS: Record<string, string> = {
  select: 'Dropdown',
  text: 'Text',
  textarea: 'Multi-line text',
  number: 'Number',
  pincode: 'Postal code',
};

export const ADDRESS_FIELD_SOURCE_LABELS: Record<string, string> = {
  masters: 'Master Data',
  google_places: 'Google Places autofill',
  manual: 'Customer types it',
};

export const ADDRESS_FIELD_MAX_SORT_ORDER = 9999;
export const FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]{1,39}$/;
const NAME_PATTERN = /^\p{L}[\p{L}\s'/-]*$/u;
const str = (v: unknown) => (v == null ? '' : String(v).trim());
export const normalizeAddressFieldName = (s: unknown) => String(s ?? '').replace(/\s+/g, ' ').trim();

export function addressFieldKeyFromName(name: string): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  return /^[a-z]/.test(slug) ? slug : '';
}

export function orderOf(row: AddressFieldExistingRecord): number {
  const top = Number(row.sortOrder);
  if (Number.isInteger(top) && top > 0) return top;
  const meta = Number(row.metadata?.sortOrder);
  return Number.isInteger(meta) && meta > 0 ? meta : 0;
}

export function nextAddressFieldSortOrder(existing: AddressFieldExistingRecord[], editingId?: string) {
  return Math.max(0, ...existing.filter((r) => r._id !== editingId).map(orderOf)) + 1;
}

export const geoOf = (row: AddressFieldExistingRecord) => str(row.metadata?.geoLevel).toLowerCase();

function repeatedTextError(label: string, text: string) {
  const run = findConsecutiveRepeatedCharacter(text);
  if (run) return `${label} cannot repeat the same character 4+ times ("${run.slice(0, 4)}")`;
  const word = findRepeatedWord(text);
  if (word) return `${label} cannot repeat the same word back to back ("${word}")`;
  return undefined;
}

/** Field errors keyed by field name (metadata keys are flattened); mirrors the server rules. */
export function validateAddressFieldForm(
  form: AddressFieldFormInput,
  ctx: { existing: AddressFieldExistingRecord[]; editingId?: string; geoLevels: AddressGeoLevelDef[]; isActive: boolean },
): Record<string, string> {
  const errors: Record<string, string> = {};
  const others = ctx.existing.filter((r) => r._id !== ctx.editingId);
  const byValue = new Map(ctx.geoLevels.map((g) => [g.value, g]));
  const geo = byValue.get(str(form.metadata.geoLevel));
  const meta = form.metadata;

  if (!geo) {
    errors.geoLevel = 'Choose where in the address this field applies';
  } else if (!geo.repeatable) {
    const clash = others.find((r) => geoOf(r) === geo.value);
    if (clash) errors.geoLevel = `${geo.label} is already configured by "${clash.name}" — edit that field instead`;
  }

  const name = normalizeAddressFieldName(form.name);
  const original = ctx.editingId ? ctx.existing.find((r) => r._id === ctx.editingId) : undefined;
  const nameUnchanged = !!original && normalizeAddressFieldName(original.name) === name;
  if (!name) errors.name = 'Display name is required';
  else if (!nameUnchanged && (name.length < 2 || name.length > 50)) errors.name = 'Display name must be 2–50 characters';
  else if (!nameUnchanged && !NAME_PATTERN.test(name)) errors.name = 'Letters, spaces, apostrophes, hyphens and "/" only — no numbers or special characters';
  else {
    const repeat = nameUnchanged ? undefined : repeatedTextError('Display name', name);
    if (repeat) errors.name = repeat;
    else {
      const clash = others.find((r) => normalizeAddressFieldName(r.name).toLowerCase() === name.toLowerCase());
      if (clash) errors.name = `"${name}" is already used by another address field`;
    }
  }

  const fieldKey = str(meta.fieldKey);
  if (geo) {
    if (!fieldKey) errors.fieldKey = 'Field Key is required';
    else if (!FIELD_KEY_PATTERN.test(fieldKey)) {
      errors.fieldKey = '2–40 characters: lowercase letters, numbers and _ — start with a letter (e.g. flat_number)';
    } else if (!geo.fieldKey && byValue.has(fieldKey) && byValue.get(fieldKey)!.fieldKey === fieldKey) {
      errors.fieldKey = `"${fieldKey}" is reserved for the ${byValue.get(fieldKey)!.label} geo level`;
    } else {
      const clash = others.find((r) => str(r.code).toLowerCase() === fieldKey || str(r.metadata?.fieldKey).toLowerCase() === fieldKey);
      if (clash) errors.fieldKey = `"${fieldKey}" is already used by "${clash.name}"`;
    }

    const fieldType = str(meta.fieldType);
    if (!fieldType) errors.fieldType = 'Choose a field type';
    else if (!geo.fieldTypes.includes(fieldType as never)) errors.fieldType = `Not allowed for ${geo.label}`;

    const source = str(meta.source);
    if (!source) errors.source = 'Choose a data source';
    else if (!geo.sources.includes(source as never)) errors.source = `Not allowed for ${geo.label}`;
  }

  const order = Number(form.sortOrder);
  if (!Number.isInteger(order) || order < 1 || order > ADDRESS_FIELD_MAX_SORT_ORDER) {
    errors.sortOrder = `Enter a whole number between 1 and ${ADDRESS_FIELD_MAX_SORT_ORDER}`;
  } else {
    const clash = others.find((r) => orderOf(r) === order);
    if (clash) {
      errors.sortOrder = `Sort order ${order} is already used by "${clash.name}" — next available is ${nextAddressFieldSortOrder(ctx.existing, ctx.editingId)}`;
    }
  }

  if (geo && !errors.sortOrder && !errors.geoLevel) {
    const required = meta.required === true;
    if (geo.parent) {
      const parentDef = byValue.get(geo.parent)!;
      const parent = others.find((r) => geoOf(r) === geo.parent);
      if (!parent) errors.geoLevel = `Add the ${parentDef.label} field first — ${geo.label} depends on it`;
      else if (ctx.isActive && parent.isActive === false) errors.geoLevel = `"${parent.name}" (${parentDef.label}) is inactive — activate it first`;
      else if (orderOf(parent) >= order) errors.sortOrder = `Must come after ${parentDef.label} — use more than ${orderOf(parent)}`;
      else if (required && parent.metadata?.required !== true) errors.required = `Cannot be required while ${parentDef.label} is optional`;
    }
    for (const child of others) {
      const childDef = byValue.get(geoOf(child));
      if (childDef?.parent !== geo.value) continue;
      if (orderOf(child) > 0 && order >= orderOf(child)) {
        errors.sortOrder = `Must come before ${childDef.label} — use less than ${orderOf(child)}`;
      } else if (!required && child.metadata?.required === true) {
        errors.required = `Must stay required while ${childDef.label} is required`;
      }
    }
  }

  const description = str(form.description);
  if (description.length > 500) errors.description = 'Description must be at most 500 characters';
  else if (description) {
    const repeat = repeatedTextError('Description', description);
    if (repeat) errors.description = repeat;
  }
  return errors;
}
